import React from 'react';
import { useCarNameLabel } from '../../../../utils/carNames';
import PageLoader from '../../../common/PageLoader';
import { applySort, SORT, useUserCoords } from '../../../../utils/sortListings';
import CarFilterPanel, {
  EMPTY_CAR_FILTER,
  carMatchesFilter,
  isCarFilterActive,
} from '../../../common/CarFilterPanel';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetCarSaleListing,
  saveAItem,
  removeSavedItem,
  getSavedItems,
} from './../../../../Redux/Actions/UserAction';
import { useTranslation } from 'react-i18next';
import SortControl from '../../../common/SortControl';
import Swal from 'sweetalert2';
import { shareItem } from './../../../../utils/shareItem';
import { buildSavedIdSet, findSavedItemId } from '../../../../utils/savedItems';

export default function UserCarForSale() {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.language || 'en').toLowerCase().startsWith('ar');
  /* [CF 2026-08-17 #11.1] The model is free text with no Arabic on the
     row; look it up from the model master. See utils/carNames.js */
  const nameLabel = useCarNameLabel(isRTL);
  // English in English mode, Arabic in Arabic mode (fallback to whichever exists).
  const L = (en, ar) => (isRTL ? ar || en : en || ar);
  const dispatch = useDispatch();
  const [sortOption, setSortOption] = useState(SORT.DISTANCE); // [CF 2026-08-02] nearest-first by default
  // [CF 2026-08-02] Car filter — same nine fields as the iOS app.
  const [carFilter, setCarFilter] = useState({ ...EMPTY_CAR_FILTER });
  const [filtersOpen, setFiltersOpen] = useState(false);
  // [CF 2026-08-02] Only asks the browser for a location when the user
  // actually picks the distance sort; cached for the session after that.
  const userCoords = useUserCoords(sortOption === SORT.DISTANCE);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    dispatch(fetCarSaleListing());
    dispatch(getSavedItems());

    // When Tap redirects back after paying to publish a car, it appends
    // ?payment=success|failed to the hash route. Show the result and refresh.
    const hash = window.location.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex !== -1) {
      const params = new URLSearchParams(hash.slice(qIndex + 1));
      const payment = params.get('payment');
      if (payment === 'success') {
        Swal.fire({
          title: 'Payment successful — your car is now published',
          icon: 'success',
          confirmButtonColor: '#3ab1f7',
        });
        dispatch(fetCarSaleListing());
      } else if (payment === 'failed') {
        Swal.fire({
          title: 'Payment failed or cancelled',
          text: 'Your car was saved but is not published yet. You can pay again from your posts.',
          icon: 'error',
          confirmButtonColor: '#3ab1f7',
        });
      }
      if (payment) {
        window.history.replaceState(null, '', hash.slice(0, qIndex));
      }
    }
  }, [dispatch]);

  const onHandleSave = (carSaleId) => {
    const isLoggedIn = sessionStorage.getItem('loged_In') === 'true';
    if (!isLoggedIn) {
      Swal.fire({
        title: 'Please Login to Save',
        text: 'You need to be logged in to save this car. Please login first.',
        icon: 'warning',
        imageUrl: './assets/images/favicon/android-chrome-192x192.png',
        imageHeight: 100,
        confirmButtonColor: '#3ab1f7',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Login',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = '#/login';
        }
      });
      return;
    }
    const currentlySaved = isSaved(carSaleId);
    setSavedOverride((prev) => ({ ...prev, [carSaleId]: !currentlySaved }));
    if (currentlySaved) {
      const savedItemId = findSavedItemId(savedItemsList, carSaleId);
      if (savedItemId) dispatch(removeSavedItem({ savedItemId }));
    } else {
      dispatch(saveAItem({ carSaleId }));
    }
  };

  const cars = useSelector((state) => state.home.carSaleList);
  const carsList = cars?.data?.response?.data;
  const savedItemsList = useSelector((state) => state?.home?.savedItemsList);
  const savedIdSet = buildSavedIdSet(savedItemsList);
  const [savedOverride, setSavedOverride] = useState({});
  const isSaved = (id) =>
    savedOverride[id] !== undefined ? savedOverride[id] : savedIdSet.has(String(id));

  const visibleList = React.useMemo(() => {
    if (!carsList) return undefined;
    let list = [...carsList];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((x) =>
        (x.brand?.name || '').toLowerCase().includes(q)
      );
    }
    list = list.filter((x) => carMatchesFilter(x, carFilter));
    list = applySort(list, sortOption, {
      name: (x) => x.brand?.name,
      price: (x) => x.price,
      coords: userCoords,
    });
    return list;
  }, [carsList, sortOption, userCoords, search, carFilter]);

  /* [CF 2026-08-17 V1] `carsList` is undefined while the request is still in
     flight, and this used to share one branch with "the list came back empty".
     The result was that Cars for Sale flashed "No cars available" for a second
     before the vehicles appeared — every other category shows a loader. Loading
     and empty are now separate states.

     The empty case deliberately does NOT early-return any more: doing so replaced
     the whole page — title, search and filter panel included — so filtering down
     to zero results left no controls to widen the filter again. The normal render
     path already prints an in-layout empty message (see `visibleList.length === 0`
     below), which is the same shape Driving Schools uses. */
  if (carsList === undefined) {
    return (
      <div className="text-center text-warning" style={{ padding: '80px' }}>
        <PageLoader />
      </div>
    );
  }

  return (
    <div>
      <div className="main-content-area">
        <div className="container">
          <h2 className="mb-2">{t('Cars for Sale')}</h2>

          <div
            className="filter-row mb-4 d-flex align-items-center flex-nowrap"
            style={{ gap: '14px' }}
          >
            {/* Sort By — left */}
            <SortControl value={sortOption} onChange={setSortOption} withPrice />

            {/* [CF 2026-08-02] Filter funnel — opens the car filter panel on its
                own row below, so this header row never reflows. */}
            <button
              type="button"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-label={t('Filter')}
              title={t('Filter')}
              style={{
                width: '42px',
                height: '42px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: 'none',
                borderRadius: '50%',
                background: isCarFilterActive(carFilter) ? '#fff7e6' : 'transparent',
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              <img
                src="assets/images/icons/filter.png"
                alt={t('Filter')}
                style={{ width: '22px', height: '22px' }}
              />
            </button>

            {/* Search (icon → expands) + result count — fills remaining space on the same row */}
            <div
              className="d-flex align-items-center"
              style={{
                gap: '12px',
                minWidth: 0,
                flex: '1 1 auto',
                justifyContent: 'flex-end',
              }}
            >
              {searchOpen ? (
                <div
                  style={{
                    position: 'relative',
                    flex: '1 1 auto',
                    maxWidth: '460px',
                    minWidth: 0,
                  }}
                >
                  <input
                    type="text"
                    autoFocus
                    className="form-control"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ paddingInlineEnd: '34px' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      setSearchOpen(false);
                    }}
                    aria-label={t('Clear')}
                    style={{
                      position: 'absolute',
                      insetInlineEnd: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      color: '#666',
                    }}
                  >
                    &times;
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t('Search by name')}
                  style={{
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    borderRadius: '50%',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <img src="assets/images/icons/search.png" alt={t('Search by name')} style={{ width: '22px', height: '22px' }} />
                </button>
              )}
            </div>
          </div>

          {/* [CF 2026-08-17 V2] FULL master lists here — `narrowToRows` is
              deliberately NOT passed.

              Do not "fix" this by passing it back. The panel can narrow each
              dropdown to what is actually on sale, and cascade it (pick Toyota,
              get Toyota's colours) — that code is still in CarFilterPanel and
              works. The client considered it and asked for the complete lists
              instead, so that every brand, year, colour and cylinder is always
              selectable. Their decision, made twice.

              The known trade-off, accepted: the masters hold 97 brands and 46
              years while the listing holds ~10 and ~5, so most combinations land
              on the empty-results message. That message stays in the layout with
              the filter panel beside it, so widening the filter is one tap away.

              This also makes Cars for Sale consistent with Car Dealers and Rent a
              Car, which never narrowed. */}
          {filtersOpen && (
            <CarFilterPanel
              value={carFilter}
              onChange={setCarFilter}
              onApply={() => setFiltersOpen(false)}
            />
          )}

          {visibleList && visibleList.length === 0 ? (
            <p className="text-muted">{t('No results match your search.')}</p>
          ) : (
            <div className="row">
              {(visibleList || []).map((car, indx) => {
                const logo = car?.businessMedia?.find(
                  (media) =>
                    media?.mediaType === 'LOGO' ||
                    media?.mediaType === 'CAR-IMAGES' ||
                    media?.mediaType === 'CAR_IMAGE' ||
                    media?.mediaType === 'IMAGES'
                )?.media?.path;
                return (
                  <div
                    key={car?.carSaleId || indx}
                    className="col-6 col-sm-6 col-md-4 col-lg-3 mb-4"
                  >
                    <div className="user-car-dealers-wrap">
                      <div className="line-a">
                        <Link to={`/user-cars-for-sale-detail/${car.carSaleId}`}>
                          <img
                            style={{
                              width: '100%',
                              height: '200px',
                              borderRadius: '10px',
                              objectFit: 'cover',
                              display: 'block',
                            }}
                            src={
                              logo ||
                              'assets/images/service-provider/car-sale/car.jpg'
                            }
                            alt={car.brand?.name || ''}
                            className="img-fluid"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src =
                                'assets/images/service-provider/car-sale/car.jpg';
                            }}
                          />
                        </Link>
                      </div>
                      <div className="line-b">
                        {/* Brand + Save/Share — one aligned row */}
                        <div className="cav-card-row">
                          <div
                            className="line-ba"
                            style={{ textAlign: isRTL ? 'right' : 'left' }}
                          >
                            {L(car?.brand?.name, car?.brand?.nameAr)}
                          </div>
                          <div className="cav-card-actions">
                            <button
                              onClick={() => onHandleSave(car?.carSaleId)}
                              title="Save"
                              className="ss-btn"
                              style={{ cursor: 'pointer', background: 'none', border: 'none', color: isSaved(car?.carSaleId) ? '#FFC400' : 'inherit', padding: 0, fontSize: 'inherit' }}
                            >
                              <i className={isSaved(car?.carSaleId) ? 'fas fa-heart' : 'far fa-heart'}></i>
                            </button>
                            <a
                              href="#"
                              onClick={(e) =>
                                shareItem(e, `/user-cars-for-sale-detail/${car.carSaleId}`, car?.carName)
                              }
                              title="Share"
                              className="ss-btn"
                            >
                              <i className="far fa-share-square"></i>
                            </a>
                          </div>
                        </div>
                        {/* Model + year — aligned to the same two columns */}
                        <div className="cav-card-row line-bb">
                          <span>{nameLabel(car?.carModel)}</span>
                          <span>{car?.modelYear?.modelYear}</span>
                        </div>
                      </div>
                      <div className="line-c">
                        <div className="cav-card-row">
                          <span>{car?.kilometerDriven} KM</span>
                          <span>KD {car?.price}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
